<template>
  <div>
    <div v-if="!panelOpen" class="cfloat">
      <div class="cbubble" role="button" tabindex="0" aria-label="打开 AI 问答助手" @click="openPanel">
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-8.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7A8.38 8.38 0 0 1 4 11.5 8.5 8.5 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z"/>
          <line x1="9" y1="10.5" x2="9.01" y2="10.5"/>
          <line x1="12" y1="10.5" x2="12.01" y2="10.5"/>
          <line x1="15" y1="10.5" x2="15.01" y2="10.5"/>
        </svg>
      </div>
    </div>
    <div v-show="panelOpen" class="cpanel" :class="{ flash: flashing }">
      <div class="cp-head">
        <span>AI 问答助手</span>
        <span class="head-actions">
          <!-- <button type="button" class="net-btn" @click="toggleNet">演示断网/恢复</button> -->
          <button type="button" class="cp-x" aria-label="关闭面板" @click="closePanel">×</button>
        </span>
      </div>
      <div v-if="embedMode" class="cp-tabs">
        <button :class="{ active: tab === 'chat' }" @click="tab = 'chat'">对话</button>
        <button :class="{ active: tab === 'search' }" @click="tab = 'search'">检索</button>
      </div>
      <div ref="cpBody" class="cp-body">
        <div v-show="tab === 'chat'">
          <div ref="chatLog" class="chat-log">
            <div v-for="(m, i) in messages" :key="i" class="msg" :class="m.role">
              <div class="b">{{ m.role === 'me' ? '我' : 'AI 助手' + (m.ctx ? ' | 结合上文' : '') }}</div>
              <div class="c" :class="{ think: m.thinking, reject: m.reject, building: m.building }" v-html="m.html || m.text" />
              <feedback-bar
                v-if="m.showFeedback"
                :question="m.question"
                :ask-record-id="m.askRecordId"
                :conversation-id="m.conversationId"
              />
              <div v-if="m.suggest && m.suggest.length" class="preset">
                <span v-for="(s, si) in m.suggest" :key="si" @click="askPreset(s)">{{ s }}</span>
              </div>
            </div>
          </div>
          <div v-if="embedMode && presets.length" class="preset preset-box">
            <span v-for="p in presets" :key="p.q" @click="askPreset(p.q)">{{ p.q }}</span>
          </div>
        </div>
        <div v-show="tab === 'search'">
          <div class="search-row">
            <el-input v-model="searchQ" placeholder="关键词检索知识库..." @keyup.enter.native="doSearch" />
            <el-button type="primary" size="small" @click="doSearch">检索</el-button>
          </div>
          <div v-for="k in searchResults" :key="k.documentId" class="doc-card" @click="openDoc(k)">
            <div class="thumb">文档</div>
            <div>
              <div class="dt">{{ k.title }} <span v-if="k._matchType === 'semantic'" class="badge admin">语义命中</span><span v-else-if="k._matchType === 'keyword'" class="badge pub">关键词命中</span></div>
              <div class="dm">{{ excerpt(k.content) }}...</div>
            </div>
          </div>
          <p v-if="!searchResults.length" class="muted">未在知识库中找到相关内容</p>
        </div>
      </div>
      <div v-show="tab === 'chat'" class="cp-input">
        <input v-model="inputQ" :disabled="!online" placeholder="输入问题..." @keydown.enter="send" />
        <button :disabled="!online" @click="send">发送</button>
      </div>
    </div>
    <doc-modal :show="docShow" :loading="docLoading" :doc="currentDoc" @close="docShow = false" />
  </div>
</template>

<script>
import { chatApi, presetApi, knowledgeApi, formatExcerpt } from '@/api'
import FeedbackBar from './FeedbackBar.vue'
import DocModal from './DocModal.vue'

export default {
  name: 'ChatFloat',
  components: { FeedbackBar, DocModal },
  props: {
    embedMode: { type: Boolean, default: false }
  },
  data() {
    return {
      panelOpen: false,
      flashing: false,
      tab: 'chat',
      inputQ: '',
      searchQ: '',
      messages: [],
      searchResults: [],
      presets: [],
      online: true,
      lastHit: null,
      docShow: false,
      docLoading: false,
      currentDoc: {},
      typingTimer: null
    }
  },
  created() {
    this.loadPresets()
    this.$root.$on('open-chat', this.onExternalOpen)
    this.$root.$on('ask-in-float', this.onExternalAsk)
  },
  beforeDestroy() {
    this.$root.$off('open-chat', this.onExternalOpen)
    this.$root.$off('ask-in-float', this.onExternalAsk)
    if (this.typingTimer) clearInterval(this.typingTimer)
  },
  methods: {
    excerpt: formatExcerpt,
    async loadPresets() {
      const res = await presetApi.list()
      this.presets = res.data || []
    },
    openPanel() {
      this.panelOpen = true
      if (!this.messages.length) this.welcome()
    },
    closePanel() {
      this.panelOpen = false
    },
    onExternalOpen() {
      this.openPanel()
      this.flash()
    },
    onExternalAsk(q) {
      this.openPanel()
      this.tab = 'chat'
      this.flash()
      if (!this.messages.length) this.welcome()
      this.inputQ = q
      this.send()
    },
    flash() {
      this.flashing = true
      setTimeout(() => { this.flashing = false }, 1200)
    },
    welcome() {
      this.messages.push({
        role: 'bot',
        text: '您好，我是 FAQ 智能问答助手。您可以问我关于设备使用、平台操作、功能说明等问题；下方也能直接检索知识库文章。'
      })
    },
    askPreset(q) {
      this.inputQ = q
      this.send()
    },
    toggleNet() {
      this.online = !this.online
      this.$message.info(this.online ? '网络已恢复' : '网络已断开，AI 问答暂不可用（重连中）')
      if (!this.online) {
        this.messages.push({
          role: 'bot',
          text: '网络已断开，正在自动重连...已显示内容将保留，重连前输入已禁用。',
          building: true
        })
      }
    },
    async send() {
      if (!this.online) {
        this.$message.warning('网络断开中，重连后可继续')
        return
      }
      const q = this.inputQ.trim()
      if (!q) return
      const clean = q.replace(/[？?。.！!，,]/g, '')
      if (!clean) {
        this.$message.warning('请输入有效的问题内容')
        return
      }
      this.inputQ = ''
      this.messages.push({ role: 'me', text: q })
      this.scrollChat()
      const botIdx = this.messages.length
      this.messages.push({ role: 'bot', thinking: true, html: '思考中<span class="dots">...</span>' })
      this.scrollChat()
      try {
        let streamed = ''
        const res = await chatApi.ask(q, { lastHit: this.lastHit }, (delta) => {
          streamed += delta
          this.messages.splice(botIdx, 1, {
            role: 'bot',
            text: streamed,
            question: q
          })
          this.scrollChat()
        })
        const data = res.data
        if (data.hitId) this.lastHit = data.hitId
        const suggest = (data.type === 'reject' || data.type === 'miss')
          ? this.presets.slice(0, 4).map(p => p.q)
          : []
        this.messages.splice(botIdx, 1, {
          role: 'bot',
          text: data.text || streamed,
          reject: data.type === 'reject',
          building: data.type === 'building',
          question: q,
          askRecordId: data.askRecordId,
          conversationId: data.conversationId || '',
          suggest,
          showFeedback: true
        })
        this.scrollChat()
      } catch (e) {
        this.messages.splice(botIdx, 1, {
          role: 'bot',
          text: e.message || '问答失败，请稍后重试',
          question: q
        })
        this.scrollChat()
      }
    },
    scrollChat() {
      this.$nextTick(() => {
        const el = this.$refs.cpBody
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    doSearch() {
      this.searchResults = []
    },
    async openDoc(k) {
      const documentId = k.documentId
      if (!documentId) return
      this.docShow = true
      this.docLoading = true
      this.currentDoc = { title: k.title || '加载中...', content: '' }
      try {
        const res = await knowledgeApi.getByDocumentId(documentId)
        this.currentDoc = res.data
      } catch (e) {
        this.$message.error(e.message || '加载文章详情失败')
        this.docShow = false
      } finally {
        this.docLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.cfloat {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 2500;
}

.cbubble {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, $blue, $lblue);
  color: #fff;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 18px rgba(26, 115, 184, 0.45);
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 2px solid $lblue;
    animation: pulse 2.4s ease-out infinite;
  }

  &:hover::after { animation-play-state: paused; }
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 0.55; }
  100% { transform: scale(1.7); opacity: 0; }
}

.cpanel {
  position: fixed;
  right: 24px;
  bottom: 82px;
  width: 372px;
  height: 540px;
  background: $surface;
  border: 1px solid $line;
  border-radius: 14px;
  box-shadow: 0 10px 40px rgba(15, 41, 66, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 2501;

  &.flash {
    animation: cpflash 1.1s ease;
  }
}

@keyframes cpflash {
  0% { box-shadow: 0 10px 40px rgba(15, 41, 66, 0.18); }
  35% { box-shadow: 0 0 0 3px $blue, 0 10px 40px rgba(15, 41, 66, 0.18); }
  100% { box-shadow: 0 10px 40px rgba(15, 41, 66, 0.18); }
}

.cp-head {
  background: linear-gradient(135deg, $navy, $blue);
  color: #fff;
  padding: 13px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 600;

  .head-actions {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .net-btn {
    background: none;
    border: 1px dashed rgba(255, 255, 255, 0.55);
    color: #DCE7F5;
    font-size: 12px;
    cursor: pointer;
    padding: 2px 8px;
    border-radius: 6px;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  }

  .cp-x {
    background: none;
    border: none;
    color: #fff;
    font-size: 18px;
    cursor: pointer;
    line-height: 1;

    &:hover { opacity: 0.85; }
  }
}

.cp-tabs {
  display: flex;
  border-bottom: 1px solid $line;
  background: $surface;

  button {
    flex: 1;
    padding: 10px;
    background: transparent;
    border: none;
    color: $grey;
    font-size: 13px;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    transition: color 0.15s, border-color 0.15s;

    &:hover { color: $blue; }

    &.active {
      color: $blue;
      border-bottom-color: $blue;
      font-weight: 600;
    }
  }
}

.cp-body {
  flex: 1;
  overflow: auto;
  padding: 14px;
  background: $bg;
}

.preset-box {
  margin-top: 10px;
}

.cp-input {
  display: flex;
  padding: 10px 12px;
  border-top: 1px solid $line;
  gap: 8px;
  background: $surface;

  input {
    flex: 1;
    padding: 9px 12px;
    border: 1px solid $line;
    border-radius: 18px;
    outline: none;
    background: $surface;
    color: $txt;
    font-size: 13px;

    &::placeholder { color: $grey; }

    &:focus {
      border-color: $blue;
      box-shadow: 0 0 0 3px rgba(26, 115, 184, 0.1);
    }

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }

  button {
    background: linear-gradient(135deg, $blue, $lblue);
    color: #fff;
    border: none;
    border-radius: 18px;
    padding: 0 16px;
    cursor: pointer;
    font-weight: 600;
    font-size: 13px;

    &:hover:not(:disabled) { filter: brightness(1.05); }
    &:disabled { opacity: 0.45; cursor: not-allowed; }
  }
}

.search-row {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

@media (max-width: 640px) {
  .cfloat { right: 14px; bottom: 14px; }

  .cpanel {
    right: 8px;
    left: 8px;
    bottom: 72px;
    width: auto;
    height: 70vh;
  }
}
</style>
